import React, { useContext, useEffect, useMemo } from 'react';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import { useSelector } from 'react-redux';
import { Box, Divider, Stack } from '@mui/material';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import { cardDimensionsMedia, palette, spaces } from '../../../assets/styles/themes/tokens';
import { Tab } from 'react-bootstrap';
import MuTabsNewUI from '../../../components/common/MuTabsNewUI';
import { ItemsTab } from '../../../components/common/MuTabsNewUI/MuTabsNewUI';
import { 
  getItemsSelector, 
  getLoadingSelector, 
  getPaginationSelector, 
} from '../../../reducers/courses.selectors';
import useAction from '../../../utils/useAction';
import { 
  fetchAllUserCourses as fetchAllUserCoursesAction,
  fetchProgressUserCourses as fetchProgressUserCoursesAction,
  fetchSuccessUserCourses as fetchSuccessUserCoursesAction
} from '../../../actions/course.actions';
import MyCoursesPageTab from './MyCoursesPageTab';
import useScreenSize from '../../../utils/useScreenSize';
import { SearchLoadingStatus } from '../../../reducers/search.selectors';
import { NotificationContext } from '../../../state/NotificationContextProvider';
import NotificationAlert, { Alert } from '../../../components/NotificationAlert/NotificationAlert';
import { zIndex } from '../../../constants';
import useCardDimensions from '../../../utils/useCardDimensions';

export enum MyCoursesTabEnum {
  ALL = 'all',
  PROGRESS = 'progress',
  SUCCESS = 'success',
}

const MyCoursesPage = () => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = React.useState<MyCoursesTabEnum>(MyCoursesTabEnum.ALL);
  const { isMdScreen, isLgScreen } = useScreenSize();
  const cardDimensions = useCardDimensions();
  const isDesktopScreen = isMdScreen || isLgScreen;
  const { notification, hideNotification, showNotification } = useContext(NotificationContext);

  const handleShowNotification = (alert: Alert) => {
    showNotification(alert);
  };

  const fetchAllCourses = useAction(fetchAllUserCoursesAction);
  const fetchProgressCourses = useAction(fetchProgressUserCoursesAction);
  const fetchSuccessCourses = useAction(fetchSuccessUserCoursesAction);

  const allCoursesPagination = useSelector(getPaginationSelector('allCourses'));
  const allCoursesLoadingState = useSelector(getLoadingSelector('allCourses'));
  const allCoursesItems = useSelector(getItemsSelector('allCourses'));

  const progressCoursesPagination = useSelector(getPaginationSelector('progressCourses'));
  const progressCoursesLoadingState = useSelector(getLoadingSelector('progressCourses'));
  const progressCoursesItems = useSelector(getItemsSelector('progressCourses'));

  const successCoursesPagination = useSelector(getPaginationSelector('successCourses'));
  const successCoursesLoadingState = useSelector(getLoadingSelector('successCourses'));
  const successCoursesItems = useSelector(getItemsSelector('successCourses'));

  const mapFetchToTab = {
    [MyCoursesTabEnum.ALL]: fetchAllCourses,
    [MyCoursesTabEnum.PROGRESS]: fetchProgressCourses,
    [MyCoursesTabEnum.SUCCESS]: fetchSuccessCourses,
  };

  const mapPaginationToTab = {
    [MyCoursesTabEnum.ALL]: allCoursesPagination,
    [MyCoursesTabEnum.PROGRESS]: progressCoursesPagination,
    [MyCoursesTabEnum.SUCCESS]: successCoursesPagination,
  };

  const mapItemsToTab = {
    [MyCoursesTabEnum.ALL]: allCoursesItems,
    [MyCoursesTabEnum.PROGRESS]: progressCoursesItems,
    [MyCoursesTabEnum.SUCCESS]: successCoursesItems,
  };

  const mapLoadingStateToTab = {
    [MyCoursesTabEnum.ALL]: allCoursesLoadingState,
    [MyCoursesTabEnum.PROGRESS]: progressCoursesLoadingState,
    [MyCoursesTabEnum.SUCCESS]: successCoursesLoadingState,
  };

  const tabs: ItemsTab<MyCoursesTabEnum>[] = [
    { 
      key: MyCoursesTabEnum.ALL, 
      name: `${t('All')}${allCoursesPagination.total !== -1 ? ` • ${allCoursesPagination.total}`: ''}`, 
      icon: 'alleTab' 
    },
    { 
      key: MyCoursesTabEnum.PROGRESS, 
      name: `${t('Started')}${progressCoursesPagination.total !== -1 ? ` • ${progressCoursesPagination.total}`: ''}`, 
      icon: 'progressTab' 
    },
    { 
      key: MyCoursesTabEnum.SUCCESS, 
      name: `${t('Completed')}${successCoursesPagination.total !== -1 ? ` • ${successCoursesPagination.total}`: ''}`, 
      icon: 'successTab' 
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(tabs[newValue].key);
  };

  useEffect(() => {
    if (allCoursesLoadingState === SearchLoadingStatus.NONE) {
      fetchAllCourses();
    }

    if (progressCoursesLoadingState === SearchLoadingStatus.NONE) {
      fetchProgressCourses();
    }

    if (successCoursesLoadingState === SearchLoadingStatus.NONE) {
      fetchSuccessCourses();
    } 
  }, [
    fetchAllCourses,
    fetchProgressCourses,
    fetchSuccessCourses,
    allCoursesLoadingState,
    progressCoursesLoadingState,
    successCoursesLoadingState
  ]);

  const memoizedNotification = useMemo(() => {
    if (!notification) return null;
    return (
      <Box sx={{ position: 'sticky', top: 0, zIndex: zIndex.alert, width: '100%' }}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <NotificationAlert
            message={notification.message}
            severity={notification.severity}
            onClose={hideNotification}
            alertSx={{
              width: '100%',
              marginLeft: `${spaces.md} !important`,
              marginRight: `${spaces.md} !important`,
              maxWidth: cardDimensions.desktop.maxWidth,
              marginX: 'auto',
            }}
          />
        </Box>
      </Box>
    );
  }, [notification, hideNotification]);

  return <MasterLayout forceNewNavigation>
    <Box
      sx={{
        backgroundColor: palette.common.white,
        paddingTop: spaces.xxs,
        minHeight: '100%'
      }}>
      {memoizedNotification}
      <Stack>
        <Box
          sx={{
            maxWidth: cardDimensionsMedia.desktop.maxWidth,
            margin: 'auto',
            width: '100%',
            paddingX: isDesktopScreen ? 0 : spaces.md,
          }}>
          <PageHeader 
            text={t('My Courses')} 
            sx={{ color: palette.text.primary }}
          />
        </Box>
        <Tab.Container
          activeKey={currentTab}
          onSelect={(key) => setCurrentTab(key as MyCoursesTabEnum)}
        >
          <Box
            sx={{
              maxWidth: cardDimensionsMedia.desktop.maxWidth,
              margin: 'auto',
              width: '100%',
              paddingX: isDesktopScreen ? 0 : spaces.md,
            }}>
            <MuTabsNewUI tabs={tabs} value={currentTab} handleChange={handleTabChange} />
          </Box>
          <Divider sx={{ position: 'relative' }} />
          <Tab.Content>
            {tabs.map((tab) => (
              <MyCoursesPageTab
                key={tab.key}
                items={mapItemsToTab[tab.key]}
                tab={tab.key}
                loadingState={mapLoadingStateToTab[tab.key]}
                pagination={mapPaginationToTab[tab.key]}
                loadMore={mapFetchToTab[tab.key]}
                onShowNotification={handleShowNotification}
              />
            ))}
          </Tab.Content>
        </Tab.Container>
      </Stack>
    </Box>
  </MasterLayout>;
};

export default MyCoursesPage;
